import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { cn } from '@tsgecart/ui';
import { useBanners } from '../../catalog/hooks/use-catalog.js';
import { recordBannerView, recordBannerClick } from '../../catalog/api/catalog-api.js';
import { StorefrontSkeleton } from '../../components/states.js';
import { Badge } from '../../components/ui/badge.js';
import type { Banner } from '../../catalog/types/catalog.js';

const CTA_CLASS =
  'focus-visible:ring-brand inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-slate-900 shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2';

/** Default overlay darkness when a banner doesn't set its own `overlayOpacity`. */
const DEFAULT_OVERLAY_OPACITY = 75;

const ALIGNMENT_CLASSES: Record<'left' | 'center' | 'right', string> = {
  left: 'items-start text-left',
  center: 'items-center text-center',
  right: 'items-end text-right',
};

const FALLBACK_BADGE = 'UP TO 50% OFF';
const FALLBACK_HEADING = 'Great Summer Sale Live Now';
const FALLBACK_SUBTITLE = 'Fresh groceries, dry fruits, cold-pressed oils and more.';
const FALLBACK_CTA = 'Shop Now';

/**
 * Hero built from `useBanners('home_hero')` (see Homepage Banner
 * Management: `badgeText`/`heading`/`subtitle`/`ctaLabel` are distinct
 * admin-authored fields -- never the same string rendered twice). If the
 * backend returns an empty array (no active/scheduled banner for today) or
 * errors, this falls back to a fixed premium-theme placeholder (never the
 * old "Demo: Homepage Hero" seed copy, since the fallback path never reads
 * banner data at all) rather than ever showing a broken or empty hero.
 */
export function HeroSection() {
  const { data, isLoading } = useBanners('home_hero');
  const banner = data?.[0];

  // Fire-and-forget view beacon, once per mounted banner id -- a simple
  // mount-time fire (no intersection observer) per the storefront wiring
  // spec; the ref guards against firing twice under React 18 StrictMode's
  // dev-only double-invoke of effects. Declared before any early return so
  // hook call order stays identical across the loading/loaded/empty renders.
  const trackedViewId = useRef<string | null>(null);
  useEffect(() => {
    if (!banner || trackedViewId.current === banner.id) return;
    trackedViewId.current = banner.id;
    recordBannerView(banner.id);
  }, [banner]);

  if (isLoading) {
    return (
      <section className="px-3 pt-4 sm:px-6 sm:pt-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <StorefrontSkeleton className="h-80 w-full sm:h-[26rem]" />
        </div>
      </section>
    );
  }

  if (!banner) {
    return (
      <section className="px-3 pb-2 pt-4 sm:px-6 sm:pt-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="relative flex min-h-[20rem] flex-col items-start justify-center gap-5 overflow-hidden rounded-b-[2.5rem] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-6 py-14 sm:min-h-[26rem] sm:rounded-b-[3rem] sm:px-14 sm:py-20">
            <div
              className="from-brand/25 pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gradient-to-br to-transparent blur-3xl"
              aria-hidden="true"
            />
            <Badge variant="brand" className="relative">
              {FALLBACK_BADGE}
            </Badge>
            <h1 className="relative max-w-xl text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
              {FALLBACK_HEADING}
            </h1>
            <p className="relative max-w-md text-sm leading-relaxed text-slate-300 sm:text-base">
              {FALLBACK_SUBTITLE}
            </p>
            <Link to={ROUTE_NAMES.shop} className={`${CTA_CLASS} relative`}>
              {FALLBACK_CTA}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return <ActiveBannerHero banner={banner} />;
}

function ActiveBannerHero({ banner }: { banner: Banner }) {
  const desktopImage = banner.mediaUrl;
  // Mobile falls back to the desktop image whenever no dedicated mobile
  // creative was uploaded -- so a banner created with only a desktop image
  // still renders correctly on small screens instead of showing nothing.
  const mobileImage = banner.mobileMediaUrl ?? banner.mediaUrl;

  const heading = banner.heading ?? banner.title;
  const ctaLabel = banner.ctaLabel ?? 'Shop now';
  const overlayOpacity = banner.overlayOpacity ?? DEFAULT_OVERLAY_OPACITY;
  const alignment = ALIGNMENT_CLASSES[banner.textAlignment ?? 'left'];

  const cta = (
    <span className={CTA_CLASS}>
      {ctaLabel}
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </span>
  );

  const content = (
    <div className="group relative overflow-hidden rounded-b-[2.5rem] bg-slate-950 sm:rounded-b-[3rem]">
      {desktopImage || mobileImage ? (
        <picture>
          {mobileImage && <source media="(max-width: 639px)" srcSet={mobileImage} />}
          <img
            src={desktopImage ?? mobileImage ?? ''}
            alt={banner.altText ?? ''}
            className="h-80 w-full object-cover sm:h-[26rem]"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        </picture>
      ) : (
        <div className="relative h-80 bg-gradient-to-br from-slate-900 via-slate-950 to-black sm:h-[26rem]">
          <div
            className="from-brand/20 pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gradient-to-br to-transparent blur-3xl"
            aria-hidden="true"
          />
        </div>
      )}

      <div
        className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10"
        style={{ opacity: overlayOpacity / 100 }}
        aria-hidden="true"
      />

      <div className={cn('absolute inset-0 flex flex-col justify-center gap-5 px-6 sm:px-14', alignment)}>
        {banner.badgeText && <Badge variant="brand">{banner.badgeText}</Badge>}
        <h1 className="max-w-xl text-3xl font-extrabold leading-tight tracking-tight text-white drop-shadow-sm sm:text-5xl">
          {heading}
        </h1>
        {banner.subtitle && (
          <p className="max-w-md text-sm leading-relaxed text-slate-200 drop-shadow-sm sm:text-base">
            {banner.subtitle}
          </p>
        )}
        {banner.linkUrl ? (
          <span className="transition group-hover:-translate-y-0.5">{cta}</span>
        ) : (
          <Link to={ROUTE_NAMES.shop} className={CTA_CLASS}>
            {ctaLabel}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        )}
      </div>
    </div>
  );

  // Fire-and-forget: the click beacon is kicked off but never awaited, so it
  // can never delay or block the click-through navigation below.
  const bannerId = banner.id;
  const handleClick = () => recordBannerClick(bannerId);

  return (
    <section className="px-3 pt-4 sm:px-6 sm:pt-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {banner.linkUrl ? (
          <a href={banner.linkUrl} aria-label={banner.altText ?? heading} onClick={handleClick} className="block">
            {content}
          </a>
        ) : (
          content
        )}
      </div>
    </section>
  );
}
